import { RichDevice, Gateway, isReady, Platform } from '@store';
import {
  Device,
  DeviceIds,
  DeviceRoles,
  FeatureDeprecated,
  FeatureNames,
  GatewayDevicesProperties,
  InstallationItemGateway,
  InstallationItemGatewayDevice,
} from '@vi-iot/client';

export function isE3BasingOnDeviceRoles(roles: string[]): boolean {
  const e3Role = 'type:E3';

  return roles.includes(e3Role);
}

export function isLegacyBasingOnDeviceRoles(roles: string[]): boolean {
  const legacyRole = 'type:legacy';
  const rolesLowercase = roles.map((role) => role.toLowerCase());

  return rolesLowercase.includes(legacyRole);
}

export function getDevicePlatformBasingOnRoles(roles: string[]): Platform {
  if (roles?.length && isE3BasingOnDeviceRoles(roles)) {
    return Platform.E3;
  } else if (roles?.length && isLegacyBasingOnDeviceRoles(roles)) {
    return Platform.Legacy;
  } else {
    return Platform.None;
  }
}

// Device service functions

export function isRoomControl(payload: { data: FeatureDeprecated[] }): boolean {
  const devicesFeature = payload.data.find(
    (feature) => feature.feature === FeatureNames.gatewayDevices
  ) as FeatureDeprecated<GatewayDevicesProperties>;
  if (isReady(devicesFeature) && devicesFeature?.properties?.devices?.value) {
    const roomControl = devicesFeature.properties?.devices?.value.find(
      (device) => device?.id === DeviceIds.RoomControl
    );

    return !!roomControl;
  }

  return false;
}

export function isHEMSDevice(payload: { data: FeatureDeprecated[] }): boolean {
  const devicesFeature = payload.data.find(
    (feature) => feature.feature === FeatureNames.gatewayDevices
  ) as FeatureDeprecated<GatewayDevicesProperties>;
  if (isReady(devicesFeature) && devicesFeature?.properties?.devices?.value) {
    const hems = devicesFeature.properties?.devices?.value.find(
      (device) => device?.id === DeviceIds.Hems
    );

    return !!hems;
  }

  return false;
}

export function hasMainDeviceRole(device: { roles?: DeviceRoles[] }): boolean {
  const mainDevicesRoles = [
    DeviceRoles.Heatpump,
    DeviceRoles.Boiler,
    DeviceRoles.BoilerRenewable,
    DeviceRoles.FuelCell,
    DeviceRoles.Ess,
    DeviceRoles.Ventilation,
    DeviceRoles.VentilationPurifier,
    DeviceRoles.EcoWater,
  ];

  const isMainDevice = device?.roles?.some((role) => mainDevicesRoles.includes(role));

  return isMainDevice && !isLagCascadeV2Device(device);
}

export function isLeadCascadeV1Device(device: Device): boolean {
  return device?.roles?.some((role) => role === DeviceRoles.CascadeLead);
}

export function isLeadCascadeV2Device(device: Device): boolean {
  return device?.roles?.some((role) => role === DeviceRoles.CascadeV2Lead);
}

export function isLagCascadeV2Device(device: { roles?: DeviceRoles[] }): boolean {
  return device?.roles?.some((role) => role === DeviceRoles.CascadeV2Lag);
}

export function isDeviceBoiler(device: Device): boolean {
  return device?.roles?.some(
    (role) => role === DeviceRoles.Boiler || role === DeviceRoles.BoilerRenewable
  );
}

export function hasMainDevice(gateway: { devices?: { roles: DeviceRoles[] }[] }): boolean {
  return Boolean(gateway.devices.filter((d) => hasMainDeviceRole(d)).length);
}

export function hasLegacyGateway(gateway: { devices?: { roles: DeviceRoles[] }[] }): boolean {
  return Boolean(gateway.devices.filter((d) => isLegacyGateway(d)).length);
}

export function isLegacyGateway(device: { roles?: DeviceRoles[] }): boolean {
  const isLegacy = device?.roles?.includes(DeviceRoles.Legacy);
  const isGateway = [
    DeviceRoles.GatewayVitoconnectOpto1,
    DeviceRoles.GatewayOT2,
    DeviceRoles.GatewayVitoconnectOpto3,
    DeviceRoles.GatewayTCU300,
    DeviceRoles.GatewayTCU200,
    DeviceRoles.GatewayTCU100,
    DeviceRoles.GatewayTCU,
    DeviceRoles.Gateway,
  ].some((role) => device?.roles?.includes(role));

  return isLegacy && isGateway;
}

export function isEcoWaterDevice(
  device: Device | InstallationItemGatewayDevice | RichDevice
): boolean {
  return device?.roles?.some((role) => role.includes(DeviceRoles.EcoWater));
}

export function isWallboxDevice(device: Device): boolean {
  return device?.roles.includes(DeviceRoles.VCS);
}

export function isSolarLogGateway(device: Device): boolean {
  return device?.roles.includes(DeviceRoles.SolarLogGateway);
}

export function isWallboxGateway(device: Device): boolean {
  return device?.roles.includes(DeviceRoles.VCS);
}

export function isSolarLogInverter(device: Device): boolean {
  return device?.roles.includes(DeviceRoles.PhotovoltaicSolarLogInverter);
}

export function isPhotovoltaic(device: Device): boolean {
  return [
    DeviceRoles.PhotovoltaicSolarLogInverter,
    DeviceRoles.PhotovoltaicCAN,
    DeviceRoles.PhotovoltaicInternal,
  ].some((role) => device?.roles?.includes(role));
}

export function getGatewayMainDevices(
  gateways: InstallationItemGateway[] | Gateway[],
  returnStandalone = true
): InstallationItemGatewayDevice[] {
  return gateways
    .map((gateway) => {
      const mainDevice = gateway.devices.find((device) => hasMainDeviceRole(device));
      const standaloneGateway = gateway.devices.find((device) => isLegacyGateway(device));

      return returnStandalone ? mainDevice ?? standaloneGateway : mainDevice;
    })
    .filter((d) => d !== undefined);
}

export function parseVersion(version: string): number[] {
  return version?.split('.').map(Number);
}
